import { Refine, Authenticated, I18nProvider } from "@refinedev/core";
import { RefineKbar, RefineKbarProvider } from "@refinedev/kbar";
import { ThemedLayoutV2, ErrorComponent } from "@refinedev/antd";
import routerBindings, {
  NavigateToResource,
  CatchAllNavigate,
  UnsavedChangesNotifier,
  DocumentTitleHandler,
} from "@refinedev/react-router-v6";
import {
  BrowserRouter,
  Routes,
  Route,
  Outlet,
  Navigate,
} from "react-router-dom";
import { App as AntdApp, ConfigProvider } from "antd";
import { useTranslation } from "react-i18next";
import viVN from "antd/locale/vi_VN";
import enUS from "antd/locale/en_US";

import { dataProvider } from "./providers/dataProvider";
import { authProvider } from "./providers/authProvider";
import { accessControlProvider } from "./providers/accessControlProvider";
import { useCustomNotificationProvider } from "./providers/notificationProvider";
import { APP_CONFIG } from "./config/app";
import { ENV } from "./config/env";
import { lightTheme, darkTheme } from "./config/theme";

// Dynamic configuration
import { buildAllResources } from "./config/resourceBuilder";
import { buildEntityRoutes } from "./config/routeBuilder";

// Static pages (non-entity)
import { Login } from "./pages/auth/Login";
import { Dashboard } from "./pages/dashboard";
import { InBasket } from "./pages/workflow/InBasket";
import { Profile } from "./pages/profile";
import { UserManuals } from "./pages/manuals";
import {
  Header,
  CustomSider,
  BottomNavBar,
  FloatingActionButton,
  DifyChatWidget,
} from "./components";
import { MobileProvider } from "./contexts/MobileContext";
import { ThemeProvider, useTheme } from "./contexts/ThemeContext";
import { TocLabelProvider } from "./contexts/TocLabelContext";
import { SiteProvider } from "./contexts/SiteContext";
import {
  GuideProvider,
  HelpCenter,
  OnboardingWelcome,
  TourManager,
} from "./features/userGuide";

import "@refinedev/antd/dist/reset.css";

const API_URL = ENV.API_URL;

const antdLocales: Record<string, typeof viVN | typeof enUS> = {
  vi: viVN,
  en: enUS,
};

/**
 * Route guard that redirects to /profile when MFA enrollment is required.
 * Wraps all authenticated routes except /profile itself.
 */
function MfaEnrollmentGuard() {
  const flag = localStorage.getItem("mfa_enrollment_required");
  if (flag === "true") {
    return <Navigate to="/profile" replace />;
  }
  return <Outlet />;
}

function AppContent() {
  const { t, i18n } = useTranslation();
  const { isDark } = useTheme();

  const i18nProvider: I18nProvider = {
    translate: (key: string, options?: Record<string, unknown>) =>
      t(key, options as Record<string, string>),
    changeLocale: (lang: string) => i18n.changeLanguage(lang),
    getLocale: () => i18n.language,
  };

  const currentLocale = antdLocales[i18n.language] || enUS;

  // Build resources dynamically from registry
  const resources = buildAllResources({ t });

  // Build entity routes dynamically
  const entityRoutes = buildEntityRoutes();

  return (
    <RefineKbarProvider>
      <ConfigProvider
        locale={currentLocale}
        theme={isDark ? darkTheme : lightTheme}
      >
        <AntdApp>
          <Refine
            dataProvider={dataProvider(API_URL)}
            authProvider={authProvider}
            accessControlProvider={accessControlProvider}
            routerProvider={routerBindings}
            notificationProvider={useCustomNotificationProvider()}
            i18nProvider={i18nProvider}
            resources={resources}
            options={{
              syncWithLocation: true,
              warnWhenUnsavedChanges: true,
              useNewQueryKeys: true,
              disableTelemetry: true,
            }}
          >
            <Routes>
              <Route
                element={
                  <Authenticated
                    key="authenticated-routes"
                    fallback={<CatchAllNavigate to="/login" />}
                  >
                    <SiteProvider>
                      <TocLabelProvider>
                        <GuideProvider>
                          <ThemedLayoutV2
                            Sider={() => <CustomSider />}
                            Header={() => <Header />}
                            Title={() => (
                              <div
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  padding: "12px",
                                  fontWeight: "bold",
                                }}
                              >
                                <img
                                  src={APP_CONFIG.appLogo}
                                  alt="Logo"
                                  style={{
                                    width: "24px",
                                    height: "24px",
                                    marginRight: "8px",
                                  }}
                                />
                                {APP_CONFIG.appName}
                              </div>
                            )}
                          >
                            <Outlet />
                          </ThemedLayoutV2>
                          {/* Mobile Navigation */}
                          <BottomNavBar />
                          <FloatingActionButton />
                          {/* User Guide Components */}
                          <HelpCenter />
                          <OnboardingWelcome />
                          <TourManager />
                          <DifyChatWidget />
                        </GuideProvider>
                      </TocLabelProvider>
                    </SiteProvider>
                  </Authenticated>
                }
              >
                <Route
                  index
                  element={<NavigateToResource resource="dashboard" />}
                />

                {/* Profile — accessible even during MFA enrollment */}
                <Route path="/profile" element={<Profile />} />

                {/* Routes guarded by MFA enrollment (redirect to /profile if enrollment required) */}
                <Route element={<MfaEnrollmentGuard />}>
                  <Route path="/dashboard" element={<Dashboard />} />
                  <Route path="/inbasket" element={<InBasket />} />
                  {ENV.USER_MANUAL_ENABLED && (
                    <Route path="/manuals" element={<UserManuals />} />
                  )}

                  {/* Dynamic entity routes from registry */}
                  {entityRoutes}

                  {/* 404 */}
                  <Route path="*" element={<ErrorComponent />} />
                </Route>
              </Route>
              <Route
                element={
                  <Authenticated key="auth-pages" fallback={<Outlet />}>
                    <NavigateToResource resource="dashboard" />
                  </Authenticated>
                }
              >
                <Route path="/login" element={<Login />} />
              </Route>
            </Routes>
            <RefineKbar />
            <UnsavedChangesNotifier />
            <DocumentTitleHandler
              handler={({ resource, action }) => {
                const appName = APP_CONFIG.appName;
                let title = appName;

                if (resource && action) {
                  const resourceName = resource.meta?.label || resource.name;
                  const actionMap: Record<string, string> = {
                    list: resourceName,
                    create: `${t("actions.create")} ${resourceName}`,
                    edit: `${t("actions.edit")} ${resourceName}`,
                    show: resourceName,
                  };
                  const pageTitle = actionMap[action] || resourceName;
                  title = `${pageTitle} | ${appName}`;
                }

                return title;
              }}
            />
          </Refine>
        </AntdApp>
      </ConfigProvider>
    </RefineKbarProvider>
  );
}

function App() {
  return (
    <BrowserRouter>
      <ThemeProvider>
        <MobileProvider>
          <AppContent />
        </MobileProvider>
      </ThemeProvider>
    </BrowserRouter>
  );
}

export default App;
